/**
 * The side panel while a run is going: its steps, what it is about, and Stop. On the first
 * make of a post the line under the title is the host's moment (their words on step 1); on a
 * later run (a change asked for, new photos, another take) it is the change note, or nothing.
 * A reel re-render says only what it is.
 */
import { useEffect, useState } from 'react';
import type { MediaMode, PostFormat } from '@str/shared';
import { stepsFrom } from '@/lib/composer-v2';

export type MakingPanelProps = {
  title: string;
  /** The post whose reel is being re-rendered from the host's edit, or null for a generation. */
  renderOf: string | null;
  statuses: string[];
  /** The host's words on step 1, kept from the three steps. */
  moment: string | null;
  /** What the host asked for on this run. */
  note: string;
  /** No version exists yet: this run is the post's first make. */
  firstMake: boolean;
  format?: PostFormat | null;
  mediaMode?: MediaMode | null;
  /** Only the caption is being rewritten: nothing about pictures is said (J28). */
  captionOnly?: boolean;
  onStop: () => void;
};

export function MakingPanel(p: MakingPanelProps) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const started = Date.now();
    const timer = window.setInterval(
      () => setElapsed(Math.floor((Date.now() - started) / 1000)),
      1000,
    );
    return () => window.clearInterval(timer);
  }, []);
  const line = p.renderOf ? null : p.firstMake ? p.moment : p.note.trim() || null;
  return (
    <>
      <h3>{p.renderOf ? `✦ ${p.title}` : 'Making it'}</h3>
      {line && (
        <p className="nx-note" style={{ marginTop: -6, color: '#111' }}>
          {line}
        </p>
      )}
      <div className="nx-steps" role="status" aria-live="polite">
        {stepsFrom(p.statuses, false).map((s) => (
          <div key={s.label} className={`nx-step ${s.state}`}>
            <span className="m">{s.state === 'done' ? '✓' : '●'}</span>
            <span>
              {s.label}
              {s.state === 'doing' ? '…' : ''}
            </span>
          </div>
        ))}
      </div>
      {elapsed >= 10 && (
        <p className="nx-note">
          {elapsed < 60
            ? `${elapsed} seconds elapsed`
            : `${Math.floor(elapsed / 60)}m ${elapsed % 60}s elapsed`}
        </p>
      )}
      <p className="nx-note">
        {p.renderOf
          ? 'Your clip, text and cover, put together as you set them. Nothing is generated.'
          : p.captionOnly
            ? 'A few seconds. Your picture stays as it is.'
            : `About ${p.format === 'Reel' ? 'two minutes' : 'a minute'}. You can leave this page; it lands in Drafts when it is done.${
                p.mediaMode === 'imagine'
                  ? ' Made from your words, marked AI.'
                  : p.mediaMode === 'photos' || !p.mediaMode
                    ? ''
                    : ' A new picture of your place, marked AI.'
              }`}
      </p>
      <div className="nx-chips">
        <button type="button" className="nx-chip" onClick={p.onStop}>
          {p.renderOf ? 'Cancel reel edit' : 'Stop'}
        </button>
      </div>
    </>
  );
}
